import {
  useCreateNonRecToRecEvent,
  useEventUpdate,
  EventsQueryKey as GetEventsQueryKey,
  EventsSearchQueryKey as SearchEventsQueryKey,
  EventEditModeEnum,
  EventEditModes,
  EventUpdate as UpdateEventDto,
  ShiftEvent,
  ShiftEventCategory,
  isTask,
  isSpot,
  Spot
} from '@domain/shifthandover'
import {PermissionType} from '@hconnect/apiclient'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {MAX_ALLOWED_ATTACHMENTS, SIDE_CARD_MIN_HEIGHT_IN_PX} from '@hconnect/common/consts'
import {useAttachmentFile} from '@hconnect/common/hooks'
import {Attachment, AttachmentFile} from '@hconnect/common/types'
import {
  getNewlyAttachedFiles,
  getUrlsForRemovedImages,
  getUniqueAttachments
} from '@hconnect/common/utils'
import {LoadingSwitcher} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {useCommonFeatureFlag, FeatureFlagKey} from '@hproduce/feature-flags'
import {checkPermission, usePermissionsParsedQuery} from '@hproduce/permissions'
import IconWarning from '@mui/icons-material/Warning'
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {EventAttachmentUpload} from '@widgets/common-shift-event'
import {AxiosError} from 'axios'
import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {HttpErrorHandlingComponentProps, withHttpErrorHandler} from '../common'
import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'
import {AnomalyMediaItem} from '../hooks/api/useAnomalyEventMedia'
import {useSpotAnomalyMedia} from '../hooks/useSpotAnomalyMedia'
import {ChecklistsInputSettings} from '../types'

import {EventEditDialog} from './dialogs'
import {EventFormContainer} from './EventFormContainer'
import {MediaThumb} from './eventProperties/AnomalyMediaViewer'

interface Props extends HttpErrorHandlingComponentProps {
  item: ShiftEvent
  plantId: string
  country: string
  doClose(original: ShiftEvent): void
  onSubmit(item: ShiftEvent): void
  checklistsInputSettings?: ChecklistsInputSettings
  onlyEditSeries?: boolean
  isEquipmentAvailable?: boolean
}

const AttachmentFileLoader = ({
  attachment,
  onReady
}: {
  attachment: Attachment
  onReady: (file: AttachmentFile) => void
}) => {
  const file: AttachmentFile = useAttachmentFile(attachment)

  useEffect(() => {
    if (file.preview) {
      onReady(file)
    }
  }, [file, onReady])

  return null
}

export const EventEdit = withHttpErrorHandler<Props>(
  ({
    item,
    doClose,
    onSubmit,
    onlyEditSeries,
    checklistsInputSettings,
    plantId,
    country,
    isEquipmentAvailable
  }) => {
    const [uniqueAttachments, setUniqueAttachments] = useState<Attachment[]>(
      getUniqueAttachments(item?.attachments, MAX_ALLOWED_ATTACHMENTS)
    )

    const [files, setFiles] = useState<AttachmentFile[]>([])

    useEffect(() => {
      setUniqueAttachments(getUniqueAttachments(item?.attachments, MAX_ALLOWED_ATTACHMENTS))
      setFiles([])
    }, [item?.attachments])

    const handleFileReady = useCallback((file: AttachmentFile) => {
      setFiles((prev) => {
        if (prev.some((f) => f.id === file.id)) return prev
        return [...prev, file]
      })
    }, [])

    const allReady = files.length === uniqueAttachments.length

    return (
      <>
        {uniqueAttachments.map((attachment) => (
          <AttachmentFileLoader
            key={attachment.id}
            attachment={attachment}
            onReady={handleFileReady}
          />
        ))}

        {allReady ? (
          <EventEditComponent
            item={item}
            filesWithPreview={files}
            doClose={doClose}
            onSubmit={onSubmit}
            onlyEditSeries={onlyEditSeries}
            checklistsInputSettings={checklistsInputSettings}
            plantId={plantId}
            country={country}
            isEquipmentAvailable={isEquipmentAvailable}
          />
        ) : (
          <ContentBox
            data-test-id="sideCard-spinner"
            isLoading
            minHeight={SIDE_CARD_MIN_HEIGHT_IN_PX}
            mode="max100PercentOfParentHeight"
          />
        )}
      </>
    )
  }
)

export const EventEditComponent = withHttpErrorHandler<
  Props & {filesWithPreview: Array<AttachmentFile>}
>(
  ({
    item,
    doClose,
    onSubmit,
    filesWithPreview,
    onlyEditSeries,
    checklistsInputSettings,
    plantId,
    country,
    isEquipmentAvailable
  }) => {
    const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
    const timezone = useUpmPlantTimezone({plantUpmId: plantId})
    const plantContext = {plantId, countryId: country}
    const isMovieAttachmentEnabled = useCommonFeatureFlag(FeatureFlagKey.MovieAttachment)
    const queryClient = useQueryClient()
    const [files, setFiles] = useState<Array<AttachmentFile>>(filesWithPreview)

    const {data: permissions} = usePermissionsParsedQuery()

    const hasPermissionChecker = useCallback(
      (permissionType: PermissionType) =>
        permissions
          ? checkPermission(permissionType, plantContext.plantId, permissions, [
              plantContext.countryId
            ])
          : false,
      [plantContext.plantId, plantContext.countryId, permissions]
    )

    const [conflictError, setConflictError] = useState<AxiosError | undefined>(undefined)
    const [mutationError, setMutationError] = useState<AxiosError | undefined>(undefined)

    const handleMutationError = useCallback((error: AxiosError) => {
      if (error.response?.status === 409) {
        setConflictError(error)
        return
      }

      setMutationError(error)
    }, [])

    const createTaskSchedule = useCreateNonRecToRecEvent({
      plantId,
      options: {onError: handleMutationError}
    })
    const updateAction = useEventUpdate(
      {plantId, timezone},
      {
        onError: handleMutationError
      }
    )

    const [editMode, setEditMode] = useState<EventEditModes | undefined>(
      onlyEditSeries ? EventEditModeEnum.SERIES : EventEditModeEnum.SINGLE
    )

    const spotEvent: Spot | null = isSpot(item) ? item : null
    const {
      orbitLink,
      mediaItems,
      hasError: hasMediaLoadError,
      isLoading: isLoadingMedia
    } = useSpotAnomalyMedia(spotEvent, plantId)
    const hasShownMediaLoadError = React.useRef(false)

    const data = updateAction.data ?? createTaskSchedule.data
    const originalAttachments = item.attachments ?? []
    const isLoading = updateAction.isPending || createTaskSchedule.isPending

    useEffect(() => {
      if (data) {
        onSubmit(data)
      }
    }, [data, onSubmit])

    if (mutationError) {
      throw mutationError
    }

    useEffect(() => {
      if (hasMediaLoadError && !hasShownMediaLoadError.current) {
        spawnErrorToast('Failed to load', {closeOnClick: true})
        hasShownMediaLoadError.current = true
      }
    }, [hasMediaLoadError])

    const clearConflictError = React.useCallback(() => {
      setConflictError(undefined)
      void queryClient.invalidateQueries({queryKey: [GetEventsQueryKey, SearchEventsQueryKey]})
      doClose(item)
    }, [setConflictError, queryClient, doClose, item])

    const doSubmit = (nextItem: UpdateEventDto) => {
      setMutationError(undefined)

      let next: UpdateEventDto = {
        ...nextItem,
        // attachment specific
        attachmentsToRemove: getUrlsForRemovedImages(originalAttachments, files),
        newAttachments: getNewlyAttachedFiles(originalAttachments, files)
      }

      // For Spot events, exclude optional fields that backend rejects when empty/none
      if (isSpot(next)) {
        const spotUpdate: Record<string, unknown> = {...next}

        // Backend requires category to be non-null for Spot events.
        // Use a neutral fallback when user leaves it empty.
        if (!next.category) {
          spotUpdate.category = ShiftEventCategory.Other
        }

        // Backend mapper fails for empty/none status on Spot events.
        // Use a safe default while keeping UI optional.
        if (!next.status || next.status === 'none') {
          spotUpdate.status = 'pending'
        }

        next = spotUpdate as UpdateEventDto
      }

      // special case were an existing non-repetitive Task is turned into a repetitive Task
      if (isTask(item) && isTask(next) && !item.repetitionInfo && !!next.repetitionInfo) {
        // collision detection on the schedule is ignored for now
        createTaskSchedule.mutate(next)
      } else {
        updateAction.mutate({
          event: next,
          scheduleRecurringTask: editMode === EventEditModeEnum.SERIES
        })
      }
    }

    return (
      <LoadingSwitcher isLoading={isLoading}>
        <Dialog
          maxWidth="xs"
          data-test-id="error-conflict-modal"
          onClose={clearConflictError}
          open={conflictError !== undefined}
        >
          <DialogTitle>
            <IconWarning color={'error'} />
            &nbsp; {t('shiftEvent.error.conflictHeading')}
          </DialogTitle>
          <DialogContent>{t('shiftEvent.error.conflictBody')}</DialogContent>
          <DialogActions>
            <Button onClick={clearConflictError} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
        {!onlyEditSeries && (
          <EventEditDialog
            isRepeating={'repetitionInfo' in item && (item.repetitionInfo?.enabled ?? false)}
            setEditMode={setEditMode}
            doClose={() => doClose(item)}
          />
        )}
        <EventFormContainer
          country={country}
          plantId={plantId}
          item={item}
          doClose={() => doClose(item)}
          doSubmit={doSubmit}
          editMode={editMode}
          checklistsInputSettings={checklistsInputSettings}
          isEquipmentAvailable={isEquipmentAvailable}
        >
          {orbitLink && (
            <Box sx={{mt: 1}}>
              <Button
                component="a"
                href={orbitLink.linkTargetUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                data-test-id="spot-media-orbit-link"
              >
                {t('shiftEvent.action.openInOrbitPortal')}
              </Button>
            </Box>
          )}
          {!isLoadingMedia && mediaItems.length > 0 && (
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1}}>
              {mediaItems.map((mediaItem: AnomalyMediaItem) => (
                <MediaThumb key={mediaItem.dataCaptureUuid} mediaItem={mediaItem} />
              ))}
            </Box>
          )}
          <EventAttachmentUpload
            setAttachments={setFiles}
            attachments={files}
            isMovieAttachmentEnabled={isMovieAttachmentEnabled}
            hasPermission={hasPermissionChecker}
            additionalCount={mediaItems.length}
          />
        </EventFormContainer>
      </LoadingSwitcher>
    )
  }
)
